import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * The mobile header drawer has one open/close lifecycle. Nested menu screens are
 * ordinary hidden sections so Back never creates another dialog, trap, or lock.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details
 * @property {HTMLDivElement} menuDrawer
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();
    this.screenStack = [];
    this.openingSummary = null;
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /** Toggle from the persistent header summary. */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /** Open the drawer and trap focus only after its optional motion completes. */
  open() {
    const { details } = this.refs;
    this.openingSummary = details.querySelector('summary');
    this.openingSummary?.setAttribute('aria-expanded', 'true');
    if (this.openingSummary?.dataset.drawerCloseLabel) {
      this.openingSummary.setAttribute('aria-label', this.openingSummary.dataset.drawerCloseLabel);
    }
    this.#resetScreens();
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      this.#afterMotion(() => trapFocus(details));
    });
  }

  /** Close from the persistent X, backdrop, or Escape. */
  close() {
    if (!this.isOpen) return;

    const { details } = this.refs;
    details.classList.remove('menu-open');
    const summary = details.querySelector('summary');
    summary?.setAttribute('aria-expanded', 'false');
    if (summary?.dataset.drawerOpenLabel) summary.setAttribute('aria-label', summary.dataset.drawerOpenLabel);
    this.#afterMotion(() => {
      this.#resetScreens();
      details.removeAttribute('open');
      removeTrapFocus();
      this.openingSummary?.focus();
      this.openingSummary = null;
    });
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const opener = event.target.closest('[data-drawer-open]');
    if (opener instanceof HTMLElement) {
      event.preventDefault();
      this.#openScreen(opener);
      return;
    }

    if (event.target.closest('[data-drawer-back]')) {
      event.preventDefault();
      this.back();
    }
  };

  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    event.preventDefault();
    this.close();
  };

  #openScreen(opener) {
    const screenId = opener.dataset.drawerOpen;
    const nextScreen = [...this.querySelectorAll('[data-drawer-screen]')]
      .find((screen) => screen.dataset.drawerScreen === screenId);
    const currentScreen = this.querySelector('.menu-drawer__screen:not([hidden])');
    if (!(nextScreen instanceof HTMLElement) || !(currentScreen instanceof HTMLElement)) return;

    this.screenStack.push({ currentScreen, opener });
    currentScreen.hidden = true;
    nextScreen.hidden = false;
    nextScreen.querySelector('[data-drawer-back], a, button')?.focus();
  }

  /** Return to the preceding screen without releasing the drawer focus trap or scroll lock. */
  back() {
    const previous = this.screenStack.pop();
    const currentScreen = this.querySelector('.menu-drawer__screen:not([hidden])');
    if (!previous || !(currentScreen instanceof HTMLElement)) return;

    currentScreen.hidden = true;
    previous.currentScreen.hidden = false;
    previous.opener.focus();
  }

  #resetScreens() {
    this.screenStack = [];
    this.querySelectorAll('[data-drawer-screen]').forEach((screen) => {
      screen.hidden = screen.dataset.drawerScreen !== 'root';
    });
  }

  #afterMotion(callback) {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      callback();
      return;
    }
    onAnimationEnd(this.refs.menuDrawer, callback, { subtree: false });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}
